import { media } from "../../design/breakpoints";
import React from "react";
import styled, { keyframes } from "styled-components";
import CustomTooltip from "../utils/CustomTooltip";
import moment from "moment";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const subtleGradient = keyframes`
  0%,100%{background-position:0% 50%;}
  50%{background-position:100% 50%;}
`;

const rainbowShadow = keyframes`
  0%{box-shadow:0 8px 30px rgba(123,189,123,0.29);}
  16%{box-shadow:0 8px 30px rgba(193,108,108,0.6);}
  32%{box-shadow:0 8px 30px rgba(209,184,138,0.6);}
  48%{box-shadow:0 8px 30px rgba(213,213,131,0.6);}
  64%{box-shadow:0 8px 30px rgba(146,146,202,0.6);}
  80%{box-shadow:0 8px 30px rgba(188,129,212,0.6);}
  100%{box-shadow:0 8px 30px rgba(130,196,130,0.6);}
`;

const textShine = keyframes`
  0%,100%{background-position:0% 50%;}
  50%{background-position:100% 50%;}
`;

const BannerWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 0.7rem;

  @media (min-width: 61.313em) {
    flex-direction: row-reverse;
    margin-top: 0;
    align-items: center;
  }
`;

const BannerContent = styled.div`
  background: linear-gradient(
    135deg,
    var(--secondary, #6c63ff) 0%,
    #e8f0ff 50%,
    var(--secondary, #6c63ff) 100%
  );
  background-size: 200% 200%;
  animation: ${subtleGradient} 10s ease infinite,
             ${fadeIn} 0.6s ease-out,
             ${rainbowShadow} 6s ease-in-out infinite;
  border-radius: 25px;
  padding: 0.5rem 1rem;
  margin: 0 auto 1rem auto;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }

  @media (min-width: 61.313em) {
    width: 80%;
  }
`;

const StyledTitle = styled.h1`
  font-family: "montserrat-medium", sans-serif;
  margin: 0.5rem auto;
  font-size: 1.3rem;
  font-weight: 600;
  position: relative;
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;

  background: linear-gradient(
    90deg,
    #0a4f47 0%,
    #107066 40%,
    #3ec9b8 50%,
    #107066 60%,
    #0a4f47 100%
  );
  background-size: 200% 100%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${textShine} 6s ease infinite;

  ${media.max480} {
    font-size: 1rem;
  }

  ${media.max360} {
    font-size: 0.85rem;
  }
`;

const Subtitle = styled.div`
  animation: ${fadeIn} 0.6s ease-out 0.3s both;
  line-height: 1.6;
  color: var(--text-color, #4a5568);
  font-size: 0.9rem;
  font-weight: 500;
  letter-spacing: 0.25px;
  opacity: 0.9;

  ${media.max640} {
    display: none;
  }
`;

const DateTime = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  margin-left: 1rem;
  white-space: nowrap;

  background: linear-gradient(
    90deg,
    var(--primary,rgb(43,60,90)) 0%,
    var(--primary,#2d3748) 40%,
    #6c63ff 50%,
    var(--primary,rgb(24,50,95)) 60%,
    var(--primary,#2d3748) 100%
  );
  background-size: 200% 100%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${textShine} 6s ease infinite;

  ${media.max480} {
    font-size: 0.7rem;
  }
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 0.5rem;
`;

function useDateTime() {
  const [now, setNow] = React.useState(new Date());

  React.useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const formatted = moment(now).format("DD MMM, h:mm a");

  return formatted;
}

export default function Banner({ title, subtitle, tooltip }) {
  const formattedDateTime = useDateTime();

  return (
    <BannerWrapper>
      <BannerContent>
        <TitleRow>
          {tooltip ? (
            <CustomTooltip content={tooltip}>
              <StyledTitle>{title}</StyledTitle>
            </CustomTooltip>
          ) : (
            <StyledTitle>{title}</StyledTitle>
          )}
          <DateTime>{formattedDateTime}</DateTime>
        </TitleRow>
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
      </BannerContent>
    </BannerWrapper>
  );
}